import { AutocompleteInput, type SelectOption } from '@components';
import type { SxProps, Theme } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { type ReactElement, useState } from 'react';

import { hasValue } from '@core/utils/equality';
import { isFormReference, lookupErrorText } from '@core/utils/formikHelpers';
import type { NestedKeyOf } from '@core/utils/types';

import { AlertDupeOptions } from './utils';

export type LookupInputProps<
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
  OptionValueType = string,
> = {
  // eslint-disable-next-line react/no-unused-prop-types -- only types `name` against the form
  formState: FState;
  name: FPath;
  label?: string;
  options: SelectOption<OptionValueType>[];

  /** @default false */
  grouped?: boolean;

  /**
   * Called with the picked `option.value`, or `null` when cleared, after the field is set,
   * so the callback may update sibling fields of the same reference without being overwritten.
   */
  onMatchChange?: (value: OptionValueType | null) => void;
  onInputChange?: (inputText: string) => void;

  /** @default empty */
  placeholder?: string;

  /** @default undefined */
  disabled?: boolean;
  tip?: string;
  sx?: SxProps<Theme>;
  readOnly?: boolean;
  validate?: (value: unknown) => string | undefined;
  isLoading?: boolean;
  noOptionsText?: string;
  /** If true, options are shown as given, e.g. when the API already filtered them. */
  disableClientSideFiltering?: boolean;
  disablePortal?: boolean;
};

/**
 * A Formik-bound single-select lookup. The field holds either the option's value or a
 * reference `{ id, option }`. Must be used inside `<Formik>`.
 */
export const LookupInput = <
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
  OptionValueType = string,
>({
  label,
  name,
  options,
  grouped = false,
  onMatchChange,
  onInputChange,
  placeholder,
  disabled,
  tip,
  sx,
  readOnly,
  validate,
  isLoading = false,
  noOptionsText,
  disableClientSideFiltering,
  disablePortal,
}: LookupInputProps<FState, FPath, OptionValueType>): ReactElement => {
  AlertDupeOptions(options);

  const [field, meta] = useField<unknown>({ name, validate });
  const { setFieldTouched, setFieldValue } = useFormikContext();

  const reference = isFormReference(field.value) ? field.value : null;
  const selectedValue: unknown = hasValue(reference) ? reference.id : field.value;
  const hasSelection = hasValue(selectedValue) && selectedValue !== '';
  const matchedOption: SelectOption<OptionValueType> | null = hasSelection
    ? (options.find((option) => option.value === selectedValue) ?? reference?.option ?? null)
    : null;

  // API search results may drop the selected option while the user types; keep showing it
  const [lastMatchedOption, setLastMatchedOption] = useState(matchedOption);
  if (hasValue(matchedOption) && matchedOption.value !== lastMatchedOption?.value) {
    setLastMatchedOption(matchedOption);
  }
  const isLastMatchSelected = hasSelection && lastMatchedOption?.value === selectedValue;
  const selectedOption = matchedOption ?? (isLastMatchSelected ? lastMatchedOption : null);

  const hasError = meta.touched && hasValue(meta.error);

  return (
    <AutocompleteInput
      name={name}
      label={label}
      tip={tip}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      sx={sx}
      options={options}
      grouped={grouped}
      value={selectedOption}
      loading={isLoading}
      noOptionsText={noOptionsText}
      disableClientSideFiltering={disableClientSideFiltering}
      disablePortal={disablePortal}
      onInputValueChange={onInputChange}
      onBlur={field.onBlur}
      error={hasError}
      helperText={hasError ? lookupErrorText(meta.error) : ' '}
      onChange={(option): void => {
        const value = option?.value ?? null;
        setFieldTouched(name);
        setFieldValue(name, hasValue(reference) ? { id: value, option } : value);
        onMatchChange?.(value);
      }}
    />
  );
};
